// src/components/RelatedAdsCarousel.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../utils/currency";
import { Heart } from "lucide-react";
import { guardImage } from "../lib/imageTrust";

// Small helpers
const cx = (...a) => a.filter(Boolean).join(" ");

function useIntersectionOnce(onEnter, options = { threshold: 0.35 }) {
  const ref = useRef(null);
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (!ref.current) return;

    const obs = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting && !triggeredRef.current) {
        triggeredRef.current = true;
        onEnter?.();
      }
    }, options);

    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [onEnter, options]);

  return ref;
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SkeletonCard = () => (
  <div
    className="related-card-skeleton"
    style={{ width: 170, minWidth: 170 }}
  >
    <div className="skeleton-image" />
    <div className="skeleton-content">
      <div className="skeleton-line w-75" />
      <div className="skeleton-line w-50" />
      <div className="skeleton-line w-66" />
    </div>
  </div>
);

/**
 * RelatedAdsCarousel
 * Props:
 * - adId (string)
 * - categoryId (string) OR categorySlug
 * - city (string) optional
 * - lang ("en"|"ar")
 * - currentAd (object) optional (to avoid showing itself)
 * - onOpenAd (ad) optional navigation callback
 */
export default function RelatedAdsCarousel({
  adId,
  categoryId,
  categorySlug,
  city,
  lang = "en",
  currentAd,
  onOpenAd,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAr = lang === "ar" || lang?.startsWith("ar");

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [headingHint, setHeadingHint] = useState("similar");
  const fetchStartedRef = useRef(false);

  // Prevent duplicate impression events per item
  const impressionSent = useRef(new Set());

  // Trigger fetch only when section is visible
  const sectionRef = useIntersectionOnce(() => {
    fetchData();
  });

  useEffect(() => {
    fetchStartedRef.current = false;
    setLoading(true);
    setItems([]);
    setError(null);

    const timer = setTimeout(() => {
      fetchData();
    }, 700);

    return () => clearTimeout(timer);
  }, [adId, categoryId, categorySlug, city]);

  const title = headingHint === 'more_in_category'
    ? t('adDetails.moreInCategory')
    : t('adDetails.similarAds');
  const sponsoredLabel = t('adDetails.sponsored');
  const seeAllLabel = t('adDetails.seeAll');

  async function fetchData() {
    if (fetchStartedRef.current) return;
    fetchStartedRef.current = true;

    try {
      setLoading(true);
      setError(null);

      // 1) Related organic ads
      const qs = new URLSearchParams();
      if (adId) qs.set("adId", adId);
      if (categoryId) qs.set("categoryId", categoryId);
      if (categorySlug) qs.set("categorySlug", categorySlug);
      if (city) qs.set("city", city);
      qs.set("limit", "10");

      const organicRes = await fetch(`${API_URL}/api/ads/related?${qs.toString()}`);
      const organicJson = organicRes.ok ? await organicRes.json() : { items: [], heading_hint: "similar" };

      // Update heading hint from backend
      if (organicJson.heading_hint) setHeadingHint(organicJson.heading_hint);

      // 2) Sponsored ads (monetized)
      const sQs = new URLSearchParams();
      sQs.set("placement", "ad_details_related");
      if (categoryId) sQs.set("categoryId", categoryId);
      if (categorySlug) sQs.set("categorySlug", categorySlug);
      if (city) sQs.set("city", city);
      sQs.set("limit", "3");

      const sponsoredRes = await fetch(`${API_URL}/api/ads/sponsored?${sQs.toString()}`);
      const sponsoredJson = sponsoredRes.ok ? await sponsoredRes.json() : { items: [] };

      const organic = Array.isArray(organicJson?.items) ? organicJson.items : [];
      const sponsored = Array.isArray(sponsoredJson?.items) ? sponsoredJson.items : [];

      // Filter out current ad if present
      const filteredOrganic = organic.filter(
        (a) => String(a?.id || a?.ad_id) !== String(adId) && 
               String(a?.id || a?.ad_id) !== String(currentAd?.ad_id)
      );

      // Normalize for UI
      const organicCards = filteredOrganic.map((a) => ({ type: "ad", ...a }));
      const sponsoredCards = sponsored.map((a) => ({ type: "sponsored", ...a, __sponsored: true }));

      // Inject sponsored at positions (after 2nd and 6th card)
      const merged = [];
      let sIndex = 0;
      const injectAfter = new Set([1, 5]);
      for (let i = 0; i < organicCards.length; i++) {
        merged.push(organicCards[i]);
        if (injectAfter.has(i) && sponsoredCards[sIndex]) {
          merged.push(sponsoredCards[sIndex++]);
        }
      }
      // Append remaining sponsored at end
      while (sponsoredCards[sIndex]) merged.push(sponsoredCards[sIndex++]);

      let finalItems = merged.slice(0, 12);

      // Fallback: latest ads if related/sponsored are empty
      if (finalItems.length === 0) {
        const fallbackQs = new URLSearchParams();
        fallbackQs.set("limit", "12");
        if (categoryId) fallbackQs.set("category", categoryId);
        if (city) fallbackQs.set("location", city);

        const fallbackRes = await fetch(`${API_URL}/api/ads?${fallbackQs.toString()}`);
        const fallbackJson = fallbackRes.ok ? await fallbackRes.json() : [];
        const fallbackItems = Array.isArray(fallbackJson)
          ? fallbackJson
          : Array.isArray(fallbackJson?.ads)
            ? fallbackJson.ads
            : Array.isArray(fallbackJson?.items)
              ? fallbackJson.items
              : [];

        finalItems = fallbackItems
          .filter((a) => String(a?.id || a?.ad_id) !== String(adId) && String(a?.id || a?.ad_id) !== String(currentAd?.ad_id))
          .slice(0, 12)
          .map((a) => ({ type: "ad", ...a }));
      }

      setItems(finalItems);
    } catch (e) {
      setError(e?.message || "Failed");
    } finally {
      setLoading(false);
    }
  }

  async function track(eventType, payload) {
    try {
      await fetch(`${API_URL}/api/ads/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventType, ...payload }),
      });
    } catch {
      // silently ignore tracking failures
    }
  }

  const handleOpen = (item) => {
    track("click", {
      placement: "ad_details_related",
      adId: String(item?.id || item?.ad_id),
      isSponsored: !!item?.__sponsored,
      sponsorshipId: item?.sponsorship_id || undefined,
      sourceAdId: String(adId),
    });

    if (onOpenAd) return onOpenAd(item);

    // Navigate to ad details
    navigate(`/ad/${item?.id || item?.ad_id}`);
  };

  const handleSeeAll = () => {
    const qs = new URLSearchParams();
    if (categoryId) qs.set("category", categoryId);
    if (categorySlug) qs.set("category", categorySlug);
    if (city) qs.set("location", city);
    navigate(`/shop?${qs.toString()}`);
  };

  // Get image URL helper — with trust guard
  const getImageUrl = (item) => {
    const img = item?.image || item?.thumbnail || 
      (Array.isArray(item?.images) ? item.images[0] : null);
    if (!img) return null;
    let url = img;
    if (!url.startsWith("http")) {
      if (url.startsWith("/uploads/")) url = `${API_URL}/api${url}`;
    }
    return guardImage(url, item?.category || categoryId || categorySlug);
  };

  return (
    <div ref={sectionRef} className="related-ads-section" dir={isAr ? "rtl" : "ltr"}>
      {/* Header row */}
      <div className="related-ads-header">
        <div className="related-ads-title">
          <span>{title}</span>
          <span className="title-dot" />
        </div>
        <button onClick={handleSeeAll} className="see-all-btn" type="button">
          {seeAllLabel}
        </button>
      </div>

      {/* Body */}
      <div className="related-ads-body">
        {loading ? (
          <div className="related-ads-scroll">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="related-ads-message">
            {t('adDetails.couldNotLoadRelated')}
          </div>
        ) : items.length === 0 ? (
          <div className="related-ads-message">
            {t('adDetails.noSimilarAds')}
          </div>
        ) : (
          <div className="related-ads-scroll">
            {items.map((item) => (
              <AdMiniCard
                key={`${item.type}-${item.id || item.ad_id}`}
                item={item}
                isAr={isAr}
                t={t}
                sponsoredLabel={sponsoredLabel}
                imageUrl={getImageUrl(item)}
                onOpen={() => handleOpen(item)}
                onImpression={() => {
                  const key = `${item.type}:${item.id || item.ad_id}`;
                  if (impressionSent.current.has(key)) return;
                  impressionSent.current.add(key);
                  track("impression", {
                    placement: "ad_details_related",
                    adId: String(item?.id || item?.ad_id),
                    isSponsored: !!item?.__sponsored,
                    sponsorshipId: item?.sponsorship_id || undefined,
                    sourceAdId: String(adId),
                  });
                }}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        .related-ads-section {
          margin-top: 14px;
          padding: 0 0 14px;
        }

        .related-ads-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .related-ads-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 16px;
          font-weight: 600;
          color: rgba(0, 0, 0, 0.9);
        }

        .title-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.2);
        }

        .see-all-btn {
          background: none;
          border: none;
          font-size: 14px;
          font-weight: 500;
          color: #195B2F;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
          transition: all 0.15s ease;
        }

        .see-all-btn:hover {
          background: rgba(21, 128, 61, 0.1);
        }

        .related-ads-body {
          position: relative;
        }

        .related-ads-scroll {
          display: flex;
          gap: 12px;
          overflow-x: auto;
          padding-bottom: 8px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .related-ads-scroll::-webkit-scrollbar {
          display: none;
        }

        .related-ads-message {
          padding: 16px;
          border-radius: 16px;
          background: white;
          border: 1px solid rgba(0, 0, 0, 0.05);
          font-size: 14px;
          color: rgba(0, 0, 0, 0.6);
          text-align: center;
        }

        /* Skeleton */
        .related-card-skeleton {
          border-radius: 16px;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.05);
          overflow: hidden;
          flex-shrink: 0;
        }

        .skeleton-image {
          height: 112px;
          background: rgba(0, 0, 0, 0.05);
          animation: pulse 1.5s ease-in-out infinite;
        }

        .skeleton-content {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .skeleton-line {
          height: 14px;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 4px;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .skeleton-line.w-75 { width: 75%; }
        .skeleton-line.w-50 { width: 50%; }
        .skeleton-line.w-66 { width: 66%; }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        /* Mini Card */
        .related-mini-card {
          width: 170px;
          min-width: 170px;
          flex-shrink: 0;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          text-align: left;
          transition: transform 0.15s ease;
        }

        .related-mini-card:hover {
          transform: translateY(-2px);
        }

        .related-mini-card:active {
          transform: scale(0.98);
        }

        [dir="rtl"] .related-mini-card {
          text-align: right;
        }

        .mini-card-inner {
          border-radius: 16px;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .mini-card-image {
          position: relative;
          height: 112px;
          background: rgba(0, 0, 0, 0.05);
        }

        .mini-card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .mini-card-no-image {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          color: rgba(0, 0, 0, 0.35);
        }

        .sponsored-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          background: #f97316;
          color: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
        }

        [dir="rtl"] .sponsored-badge {
          left: auto;
          right: 8px;
        }

        .mini-card-heart {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.05);
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(0, 0, 0, 0.4);
        }

        [dir="rtl"] .mini-card-heart {
          right: auto;
          left: 8px;
        }

        .mini-card-content {
          padding: 12px;
        }

        .mini-card-title {
          font-size: 14px;
          font-weight: 600;
          color: rgba(0, 0, 0, 0.85);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mini-card-price {
          margin-top: 4px;
          font-size: 14px;
          font-weight: 700;
          color: #195B2F;
        }

        .mini-card-location {
          margin-top: 4px;
          font-size: 12px;
          color: rgba(0, 0, 0, 0.55);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mini-card-no-location {
          margin-top: 4px;
          font-size: 12px;
          color: rgba(0, 0, 0, 0.35);
        }
      `}</style>
    </div>
  );
}

function AdMiniCard({ item, isAr, t, sponsoredLabel, imageUrl, onOpen, onImpression }) {
  const ref = useIntersectionOnce(() => onImpression?.(), { threshold: 0.6 });

  const price = formatPrice(item?.price, item?.currency || "SDG", isAr ? "ar" : "en");
  const location = item?.city || item?.location || item?.state || "";

  return (
    <button
      ref={ref}
      type="button"
      onClick={onOpen}
      className="related-mini-card"
      data-testid="related-ad-card"
    >
      <div className="mini-card-inner">
        <div className="mini-card-image">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={item?.title || "ad"}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="mini-card-no-image">
              {t('adDetails.noImage')}
            </div>
          )}

          {/* Sponsored badge */}
          {item?.__sponsored && (
            <div className="sponsored-badge">
              {sponsoredLabel}
            </div>
          )}

          {/* Heart icon */}
          <div className="mini-card-heart">
            <Heart size={14} />
          </div>
        </div>

        <div className="mini-card-content">
          <div className="mini-card-title">
            {item?.title || t('adDetails.ad')}
          </div>

          <div className="mini-card-price">
            {price || t('adDetails.priceNotSet')}
          </div>

          {location ? (
            <div className="mini-card-location">{location}</div>
          ) : (
            <div className="mini-card-no-location">
              {t('adDetails.locationNotSet')}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
